import { ALLOCATION_DIMENSION } from "constants/action-types";
import { Observable } from "rxjs";
import { ActionsObservable } from "redux-observable";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import {
    CreateAllocationDimensionValueRequestAction,
    createAllocationDimensionValueComplete,
    UpdateAllocationDimensionValueRequestAction,
    updateAllocationDimensionValueComplete,
    SortAllocationDimensionRequestValueAction,
    sortAllocationDimensionValueComplete,
} from "actions/allocation-dimension";
import { formatMessage } from "lib/globalize-util";
import {
    AllocationDimensionOwnerObject,
    buildMutateOperation,
    AllocationDimensionOperationKey,
} from "helpers/allocation-dimension";
import { add as snackbarAdd } from "actions/snackbars";

export const createAllocationDimensionValue = (
    action$: ActionsObservable<CreateAllocationDimensionValueRequestAction>
) =>
    action$.ofType(ALLOCATION_DIMENSION.VALUES.CREATE.REQUEST).switchMap(({ payload, meta }) => {
        const { index, allocationDimensionValue, allocationDimension } = payload;
        const { objectId, owner } = meta;
        let path;
        switch (owner) {
            case AllocationDimensionOwnerObject.Merchant:
                path = `${siteConfig.goAPI}/v1/collections/merchants/${objectId}/allocation_dimensions/${allocationDimension.id}`;
                break;
            default:
                return [
                    createAllocationDimensionValueComplete(new Error(`Unknown owner type: ${owner}`), objectId, owner),
                ];
        }

        return ajax({
            method: "PUT",
            url: path,
            body: {
                operations: [
                    buildMutateOperation(AllocationDimensionOperationKey.InsertDimensionValue, {
                        index,
                        value: allocationDimensionValue,
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown create error"));
                }
                return [
                    createAllocationDimensionValueComplete(response, objectId, owner),
                    snackbarAdd({
                        message: formatMessage(
                            `Merchant allocation dimension value: ${allocationDimensionValue.name} created`
                        ),
                    }),
                ];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(createAllocationDimensionValueComplete(error, objectId, owner)),
                    Observable.of(
                        snackbarAdd({
                            message: error.message || formatMessage("Error creating allocation dimension value"),
                        })
                    )
                )
            );
    });

export const updateAllocationDimensionValue = (
    action$: ActionsObservable<UpdateAllocationDimensionValueRequestAction>
) => {
    let buffers = {};
    return action$
        .ofType(ALLOCATION_DIMENSION.VALUES.UPDATE.REQUEST)
        .map((action) => {
            const { meta } = action;

            // buffers example: { "123Merchant": [action1, action2], "124Merchant": [action3, action4] }
            const bufferKey = `${meta.objectId}${meta.owner}`;
            if (!buffers[bufferKey]) {
                buffers[bufferKey] = [action];
            } else {
                buffers[bufferKey] = buffers[bufferKey].concat([action]);
            }

            return action;
        })
        .debounce(() => Observable.interval(50))
        .map(() => {
            const groupedBufferedActions = Object.keys(buffers).map((key) => buffers[key]);
            buffers = {};

            // This is array of array. Eg: [[action1, action2], [action3, action4]]
            return groupedBufferedActions;
        })
        .flatMap((groupedBufferedActions) => {
            return Observable.from(groupedBufferedActions).flatMap((bufferedActions) => {
                // bufferedActions contains actions with same allocationDimension.id, objectId and owner
                // See how they were grouped before debounce. Thus, we can take first element to represent the whole actions
                const { payload, meta } = bufferedActions[0];

                const { allocationDimension } = payload;
                const { objectId, owner } = meta;
                let path;
                switch (owner) {
                    case AllocationDimensionOwnerObject.Merchant:
                        path = `${siteConfig.goAPI}/v1/collections/merchants/${objectId}/allocation_dimensions/${allocationDimension.id}`;
                        break;
                    default:
                        return [
                            updateAllocationDimensionValueComplete(
                                new Error(`Unknown owner type: ${owner}`),
                                objectId,
                                owner
                            ),
                        ];
                }

                const operations = bufferedActions.reduce((acc, action) => {
                    const { payload: _payload } = action;
                    acc.push(
                        buildMutateOperation(AllocationDimensionOperationKey.SetDimensionValue, {
                            index: _payload.index,
                            value: _payload.allocationDimensionValue,
                        })
                    );

                    return acc;
                }, []);

                return ajax({
                    method: "PUT",
                    url: path,
                    body: {
                        operations,
                    },
                })
                    .flatMap(({ response, status }) => {
                        if (status !== 200) {
                            throw new Error(response.message || formatMessage("Unknown update error"));
                        }

                        return [updateAllocationDimensionValueComplete(response, objectId, owner)];
                    })
                    .catch((error) =>
                        Observable.merge(
                            Observable.of(updateAllocationDimensionValueComplete(error, objectId, owner)),
                            Observable.of(
                                snackbarAdd({
                                    message: error.message || formatMessage("Error updating allocation dimension"),
                                })
                            )
                        )
                    );
            });
        });
};

export const sortAllocationDimensionValue = (action$: ActionsObservable<SortAllocationDimensionRequestValueAction>) =>
    action$.ofType(ALLOCATION_DIMENSION.VALUES.SORT.REQUEST).switchMap(({ payload, meta }) => {
        const { index, destinationIndex, allocationDimension } = payload;
        const { objectId, owner } = meta;
        let path;
        switch (owner) {
            case AllocationDimensionOwnerObject.Merchant:
                path = `${siteConfig.goAPI}/v1/collections/merchants/${objectId}/allocation_dimensions/${allocationDimension.id}`;
                break;
            default:
                return [
                    sortAllocationDimensionValueComplete(new Error(`Unknown owner type: ${owner}`), objectId, owner),
                ];
        }

        return ajax({
            method: "PUT",
            url: path,
            body: {
                operations: [
                    buildMutateOperation(AllocationDimensionOperationKey.MoveDimensionValue, {
                        index,
                        destination_index: destinationIndex,
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown update error"));
                }

                return [sortAllocationDimensionValueComplete(response, objectId, owner)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(sortAllocationDimensionValueComplete(error, objectId, owner)),
                    Observable.of(
                        snackbarAdd({ message: error.message || formatMessage("Error updating allocation dimension") })
                    )
                )
            );
    });
